/**
 * Is this output the WHOLE translation, or a fragment of one?
 *
 * INCIDENT-20260824-C established this check on the backend deliverable hook
 * (functions/src/prest0nSession/translationDeliverableHook.ts). It was never
 * added here — and there are TWO translation lanes. On 2026-08-25 a 30-page
 * clinical record went down THIS one and shipped a 3KB PDF and an 8KB DOCX
 * with nothing to stop them.
 *
 * The pull request that introduced the backend gate said, about the three
 * PDF readers, that two copies of one rule drifting apart is how code rots
 * separately. The gate then landed in one lane only. Hence the drift test
 * pinning these constants to the backend's.
 */

/**
 * Below this, the source is a pasted snippet or a one-page letter and the
 * ratio is meaningless — a two-line note translates to two lines. At or
 * above it we are packaging a DOCUMENT and coverage applies.
 */
export const DOCUMENT_SOURCE_MIN_CHARS = 2_000;

/**
 * Minimum output-to-source character ratio for a document translation.
 *
 * Real EN↔ES translations run roughly 0.8–1.3× the source: Spanish is the
 * wordier language, so an ES→EN pass usually SHRINKS. 0.35 sits far below
 * any honest translation and far above the 0.006 the incident shipped, so
 * it separates the two cases without adjudicating translation style.
 */
export const MIN_TRANSLATION_COVERAGE_RATIO = 0.35;

export interface TranslationCompletenessVerdict {
  complete: boolean;
  /** Machine-readable cause, for logs and tests. Null when complete. */
  reason: string | null;
  ratio: number;
}

/**
 * A deliberately narrow question: is the output implausibly SMALL for the
 * source it was made from? Nothing here judges translation quality, style or
 * accuracy — only that a document-sized input did not produce a fragment.
 */
export function assessTranslationCompleteness(
  sourceText: string,
  translatedText: string,
): TranslationCompletenessVerdict {
  const source = String(sourceText || '').trim();
  const output = String(translatedText || '').trim();

  // No source to measure against (inline snippet, prior workflow output,
  // pasted text): the ratio would be noise, so it is not consulted.
  if (source.length < DOCUMENT_SOURCE_MIN_CHARS) {
    return { complete: true, reason: null, ratio: 1 };
  }

  if (!output) {
    return { complete: false, reason: 'empty_translation', ratio: 0 };
  }

  const ratio = output.length / source.length;
  if (ratio < MIN_TRANSLATION_COVERAGE_RATIO) {
    return { complete: false, reason: `translation_too_short:${ratio.toFixed(4)}`, ratio };
  }

  return { complete: true, reason: null, ratio };
}
